import 'react-autocomplete-input/dist/bundle.css';

import { useState } from 'react';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AGGR_FUNCTION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { Feature } from 'src/WebApiController';

import * as styles from '../../TableColumns.css';
import { AggregateFunction } from '../TableCustomColumnModal.types';
import {
  FormulaBuilderShortcuts,
  FormulaTokenDisplay,
} from './FormulaBuilderInput.components';
import { useFormulaBuilderActions } from './FormulaBuilderInput.hooks';

type FormulaBuilderInputProps = {
  /**
   * Key: display name, Value: column id
   * For example in listing: { 'Original': ListingTableColumnId.TicketCount }
   */
  fieldOptionsReverse: Record<string, string>;
  /**
   * Each token is a number literal, operator, or column id
   * For example in listing: ['ticketCount', '+', '20']
   *
   * In the UI it will show ["Original", "+", "20"]
   */
  formulaTokens: string[];
  setFormulaTokens: (tokens: string[]) => void;
  formulaCaretPosition: number;
  setFormulaCaretPosition: (position: number) => void;
  showMetricSelector: boolean;
  setShowMetricSelector: (show: boolean) => void;
  aggregateFunction?: AggregateFunction | undefined;
  setAggregateFunction?: (func: AggregateFunction) => void | undefined;
};

export function FormulaBuilderInput({
  fieldOptionsReverse,
  formulaTokens,
  setFormulaTokens,
  formulaCaretPosition,
  setFormulaCaretPosition,
  showMetricSelector,
  setShowMetricSelector,
  aggregateFunction,
  setAggregateFunction,
}: FormulaBuilderInputProps) {
  const hasAvgCntAggFeature = useUserHasFeature(Feature.AvgCntForReports);
  const [showMathOperatorSelector, setShowMathOperatorSelector] =
    useState<boolean>(false);

  const {
    formulaTokensWithCaret,
    formulaError,
    onBuilderInputClick,
    onRegisterTokenRef,
  } = useFormulaBuilderActions(
    formulaTokens,
    formulaCaretPosition,
    setFormulaTokens,
    setFormulaCaretPosition,
    setShowMetricSelector,
    setShowMathOperatorSelector
  );

  return (
    <>
      <FormulaBuilderShortcuts
        fieldOptionsReverse={fieldOptionsReverse}
        formulaTokens={formulaTokens}
        setFormulaTokens={setFormulaTokens}
        formulaCaretPosition={formulaCaretPosition}
        setFormulaCaretPosition={setFormulaCaretPosition}
      />
      <PosFormField errors={formulaError?.message}>
        <div className={styles.autoCompleteInputWrapper}>
          <div
            tabIndex={-1}
            contentEditable={false}
            className={styles.formulaEditor}
            onClick={onBuilderInputClick}
          >
            {formulaTokensWithCaret.map((token, i) => (
              <FormulaTokenDisplay
                index={i}
                key={token + i.toString()}
                elementRef={onRegisterTokenRef(i)}
                token={token}
                formulaTokens={formulaTokens}
                formulaCaretPosition={formulaCaretPosition}
                fieldOptionsReverse={fieldOptionsReverse}
                setFormulaTokens={setFormulaTokens}
                setFormulaCaretPosition={setFormulaCaretPosition}
                showMetricSelector={showMetricSelector}
                setShowMetricSelector={setShowMetricSelector}
                showMathOperatorSelector={showMathOperatorSelector}
                setShowMathOperatorSelector={setShowMathOperatorSelector}
              />
            ))}
          </div>
          {hasAvgCntAggFeature && aggregateFunction && setAggregateFunction && (
            <PosEnumSelect
              value={aggregateFunction}
              onChange={(value) => {
                if (value) {
                  setAggregateFunction(value);
                }
              }}
              valueOptionsContent={AGGR_FUNCTION_TO_CID}
              style={{ textTransform: 'uppercase', width: '30%' }}
            />
          )}
        </div>
      </PosFormField>
    </>
  );
}
